/**
 * Routing service using OSRM via backend API.
 *
 * Provides real driving/cycling distances and route geometries
 * instead of bird's-eye (Haversine) distances.
 */

// Route cache - keyed by "fromLat,fromLon,toLat,toLon"
const routeCache = new Map();
const MAX_CACHE_SIZE = 100;

function getCacheKey(fromLat, fromLon, toLat, toLon) {
    return `${fromLat.toFixed(4)},${fromLon.toFixed(4)},${toLat.toFixed(4)},${toLon.toFixed(4)}`;
}

/**
 * Get route from cache or fetch from API
 * @param {number} fromLat
 * @param {number} fromLon
 * @param {number} toLat
 * @param {number} toLon
 * @param {string} profile - 'car', 'bike', or 'foot'
 * @returns {Promise<Object|null>} - Route object with distance_km, duration_min, geometry
 */
export async function getRoute(fromLat, fromLon, toLat, toLon, profile = 'car') {
    const cacheKey = getCacheKey(fromLat, fromLon, toLat, toLon);

    // Check cache first
    if (routeCache.has(cacheKey)) {
        return routeCache.get(cacheKey);
    }

    try {
        const params = new URLSearchParams({
            from_lat: fromLat.toString(),
            from_lon: fromLon.toString(),
            to_lat: toLat.toString(),
            to_lon: toLon.toString(),
            profile,
        });

        const response = await fetch(`/api/route?${params}`);
        if (!response.ok) {
            console.warn(`Route API returned ${response.status}`);
            return null;
        }

        const data = await response.json();

        if (data.error) {
            console.warn(`Route API error: ${data.error}`);
            if (window.updateApiStatus) window.updateApiStatus('routing', false);
            return null;
        }

        // Mark routing API as healthy
        if (window.updateApiStatus) window.updateApiStatus('routing', true);

        // Cache the result
        if (routeCache.size >= MAX_CACHE_SIZE) {
            // Remove oldest entry
            const firstKey = routeCache.keys().next().value;
            routeCache.delete(firstKey);
        }
        routeCache.set(cacheKey, data);

        return data;
    } catch (err) {
        console.error('Failed to fetch route:', err);
        if (window.updateApiStatus) window.updateApiStatus('routing', false);
        return null;
    }
}

/**
 * Decode a polyline string into an array of [lat, lon] coordinates
 * Uses the Google Polyline Algorithm format
 * @param {string} encoded - Encoded polyline string
 * @returns {Array<[number, number]>} - Array of [lat, lon] pairs
 */
export function decodePolyline(encoded) {
    const points = [];
    let index = 0;
    let lat = 0;
    let lon = 0;

    while (index < encoded.length) {
        // Decode latitude
        let shift = 0;
        let result = 0;
        let byte;
        do {
            byte = encoded.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
        } while (byte >= 0x20);
        lat += (result & 1) ? ~(result >> 1) : (result >> 1);

        // Decode longitude
        shift = 0;
        result = 0;
        do {
            byte = encoded.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
        } while (byte >= 0x20);
        lon += (result & 1) ? ~(result >> 1) : (result >> 1);

        points.push([lat / 1e5, lon / 1e5]);
    }

    return points;
}
